

































































































































































































































































































import { Component, Vue } from 'vue-property-decorator';
import secondaryNav from '@/components/navbars/secondary-nav.vue';
import {
	Audit,
	FeedTank,
	Sensor,
	SensorsApi,
	Tank,
	Vat,
	WaterMeter,
	WaterMonitor,
	WaterTank,
} from '../../../api';
import TitleMixin from '@/mixins/title-mixin';
import RouterLinkButton from '@/components/elements/buttons/router-link-button.vue';
import infoTabs from '@/layouts/parent/BTabsParent.vue';
import AdminChart from '@/components/charts/admin-chart.vue';
import SensorRawData from '@/components/tables/sensorRawData.vue';
import { AxiosResponse } from 'axios';
import { Constants } from '@/common/Constants';
import StatusRow from '@/components/display/information-rows/status-row.vue';
import ChangelogLink from '@/components/display/changelog-link.vue';
import ConfirmModal from '@/components/display/confirm-modal.vue';
import Toast from '@/components/display/toast.vue';
import { ApiUtils } from '@/common/ApiUtils';

@Component({
	components: {
		ConfirmModal,
		ChangelogLink,
		StatusRow,
		RouterLinkButton,
		secondaryNav,
		infoTabs,
		AdminChart,
		SensorRawData,
	},
	mixins: [TitleMixin],
})
export default class SensorView extends Vue {
	public smallScreen = window.innerWidth > 1000;
	public loading = true;
	public $bvToast: any;
	public sensor:
		| (Sensor & {
				audits: Audit[];
				owner: WaterMeter | WaterTank | WaterMonitor | FeedTank | null;
				vat?: Vat | null;
				tank?: Tank | null;
		  })
		| null = null;
	public site: any | null = null;
	public next: any = '';
	public prev: any = '';
	public rawData: any[] | null = [];
	public audits: any[] | null = null;
	public fields: any[] | null = [
		{ key: 'created_at', label: 'Date Sent' },
		{ key: 'from', label: 'From' },
		{ key: 'to', label: 'To' },
		{ key: 'user.name', label: 'By' },
	];
	public sensors: any[] | null = [];
	public dash: any[] | null = [];
	private api: SensorsApi;
	private toast: Toast = new Toast();

	public backgroundCalibrateLoading: boolean = false;

	constructor() {
		super();
		this.api = new SensorsApi();
	}

	public get sensorOwner() {
		return this.sensor?.owner ?? this.sensor?.vat ?? this.sensor?.tank ?? null;
	}

	public get sensorEntity(): {
		url: string;
		name: string;
		params: { [key: string]: string };
	} | null {
		const owner = this.sensorOwner;
		const ownerType =
			this.sensor?.ownerType ??
			(this.sensor?.vat
				? Constants.ENTITY_TYPE_MILK_VAT
				: this.sensor?.tank
				? Constants.ENTITY_TYPE_FUEL_TANK
				: null);
		if (owner && ownerType) {
			return {
				url: this.getEntityUrl(ownerType, owner),
				name: this.getEntityName(ownerType, owner),
				params: {
					productId: owner.productId!.toString(),
					...this.getEntityParams(ownerType, owner),
				},
			};
		}
		return null;
	}

	public get isDistanceSensor(): boolean {
		if (!this.sensor) {
			return false;
		}
		return ['distance', 'lidarDistance', 'radarDistance', 'lvLogicsDistance'].includes(
			this.sensor.type ?? '',
		);
	}

	public pageTitle() {
		return `Sensor | ${this.sensor ? this.sensor.serialNumber : ''}`;
	}

	public async load() {
		const { sensorId } = this.$route.params;
		this.loading = true;
		try {
			const { data } = (await this.api.getSensorById(parseInt(sensorId, 10), {
				params: {
					include:
						'site,audits,audits.user,transceiver.site,transceiver.products,tank.product.site,vat.product.site,node.entitylocation,owner.product.site',
				},
			})) as AxiosResponse<
				Sensor & {
					audits: Audit[];
					owner: WaterMeter | WaterTank | WaterMonitor | FeedTank | null;
					vat?: Vat | null;
					tank?: Tank | null;
				}
			>;
			this.sensor = data || {};
			this.audits = data.audits || {};

			if (this.sensor?.type === 'capacitance') {
				await this.loadDash();
				try {
					const sensorData = await this.api.getSensorProperties(parseInt(sensorId, 10));
					if (!sensorData?.data?.data) {
						return;
					}
					this.sensor.emptyCapacitance = sensorData.data.data[0].data?.emptyCapacitance;
					this.sensor.probeLength = sensorData.data.data[0].data?.length;
					this.sensor.dimension = sensorData.data.data[0].data?.dimension;
				} catch (e) {
					console.log(e);
				}
			}

			await this.reload(null, false);
		} catch (e) {
			console.log(e);
			this.sensor = null;
		} finally {
			this.loading = false;
		}
	}

	public async loadDash() {
		const { sensorId } = this.$route.params;
		try {
			const api = new SensorsApi();
			const response = await api.getSensorDash(parseInt(sensorId, 10));
			this.dash = response.data.data;
		} catch (e) {
			console.log(e);
		}
	}

	public async reload(sortKey?: string | null, prev = false) {
		const { sensorId } = this.$route.params;
		try {
			const api = new SensorsApi();
			const response = await api.getSensorData(parseInt(sensorId, 10), {
				params: { sortKey, prev },
			});
			this.rawData = response.data.data ?? [];

			this.next = response.data?.links?.nextSort ?? '';
			this.prev = response.data?.links?.currentSortKey ?? '';
		} catch (e) {
			console.log(e);
		}
	}

	public async back() {
		this.reload(this.prev, true);
	}

	public async forward() {
		this.reload(this.next, false);
	}

	public created() {
		this.load();
	}

	private getEntityUrl(
		entityType: string,
		entity?: Vat | Tank | WaterMeter | WaterTank | WaterMonitor | FeedTank | null,
	): string {
		switch (entityType) {
			case Constants.ENTITY_TYPE_MILK_VAT: {
				return 'products.milk.vat';
			}
			case Constants.ENTITY_TYPE_FUEL_TANK: {
				return 'products.fuel.tank.view';
			}
			case Constants.ENTITY_TYPE_WATER_METER:
			case 'waterMeters':
			case Constants.ENTITY_TYPE_WATER_TANK:
			case 'waterTanks':
			case Constants.ENTITY_TYPE_WATER_MONITOR:
			case 'waterMonitors':
				return 'products.water.entity.view';
			case Constants.ENTITY_TYPE_FEED_TANK: {
				return 'products.feed.feedtanks.tankdetail';
			}
			default:
				return '';
		}
	}

	private getEntityName(
		entityType: string,
		entity?: Vat | Tank | WaterMeter | WaterTank | WaterMonitor | FeedTank | null,
	): string {
		switch (entityType) {
			case Constants.ENTITY_TYPE_MILK_VAT: {
				return (entity as Vat).label ?? '';
			}
			case Constants.ENTITY_TYPE_FUEL_TANK: {
				return (entity as Tank).name ?? '';
			}
			case Constants.ENTITY_TYPE_WATER_METER:
			case 'waterMeters':
				return (entity as WaterMeter).name ?? 'Water Meter';
			case Constants.ENTITY_TYPE_WATER_TANK:
			case 'waterTanks':
				return (entity as WaterTank).name ?? 'Water Tank';
			case Constants.ENTITY_TYPE_WATER_MONITOR:
			case 'waterMonitors':
				return (entity as WaterMonitor).name ?? 'Water Monitor';
			case Constants.ENTITY_TYPE_FEED_TANK: {
				return (entity as FeedTank).name ?? '';
			}
			default:
				return '';
		}
	}

	private getEntityParams(
		entityType: string,
		entity?: Vat | Tank | WaterMeter | WaterTank | WaterMonitor | FeedTank | null,
	): { [key: string]: string } {
		const ownerId: string = entity?.id?.toString() ?? '0';
		switch (entityType) {
			case Constants.ENTITY_TYPE_MILK_VAT: {
				return { vatId: ownerId };
			}
			case Constants.ENTITY_TYPE_FUEL_TANK: {
				return { tankId: ownerId };
			}
			case Constants.ENTITY_TYPE_WATER_METER:
			case 'waterMeters':
				return { entityType: 'water_meters', entityId: ownerId };
			case Constants.ENTITY_TYPE_WATER_TANK:
			case 'waterTanks':
				return { entityType: 'water_tanks', entityId: ownerId };
			case Constants.ENTITY_TYPE_WATER_MONITOR:
			case 'waterMonitors':
				return { entityType: 'water_monitors', entityId: ownerId };
			case Constants.ENTITY_TYPE_FEED_TANK: {
				return { feedTankId: ownerId };
			}
			default:
				return {};
		}
	}

	public async sendBackgroundCalibrate(params: { modalId?: string }): Promise<void> {
		this.backgroundCalibrateLoading = true;

		try {
			if (this.sensor?.id) await this.api.publishBackgroundModel(+this.sensor.id!);
			if (params?.modalId) this.$bvModal.hide(params.modalId);
		} catch (ex) {
			const { errorMessage } = ApiUtils.handleApiError(
				ex,
				'There was an error sending background calibrate message to transceiver',
			);
			this.toast.makeToast(errorMessage, 'Error', 'danger');
		}

		this.backgroundCalibrateLoading = false;
	}
}
